// src/TaxesSettings.js
import React, { useState, useEffect } from "react";
import TitleBar from "./components/ui/TitleBar";
import NotificationOverlayFrame from "./components/NotificationOverlayFrame";
import useTimedUnsavedWarning from './useTimedUnsavedWarning';

import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();

const RequiredMark = () => <span style={{ color: '#ef4444' }} aria-hidden="true"> *</span>;

// ── Mobile helper ──────────────────────────────────────────────────────────
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const DEFAULTS = {
  self_employment_tax_rate: 15.3,
  state_income_tax_rate: 0,
  mileage_rate: 0.725,
  mileage_rates: [],
  states: [],
};

const normalize = (payload) => {
  const d = payload && typeof payload === 'object' ? payload : {};
  return {
    self_employment_tax_rate: Number(d.self_employment_tax_rate ?? DEFAULTS.self_employment_tax_rate),
    state_income_tax_rate: Number(d.state_income_tax_rate ?? DEFAULTS.state_income_tax_rate),
    mileage_rate: Number(d.mileage_rate ?? DEFAULTS.mileage_rate),
    mileage_rates: Array.isArray(d.mileage_rates) ? d.mileage_rates.map(rate => ({
      id: Number(rate.id),
      effective_date: String(rate.effective_date || ''),
      rate_per_mile: Number(rate.rate_per_mile),
      reference_count: Number(rate.reference_count || 0),
      is_used: Boolean(rate.is_used),
    })) : [],
    states: Array.isArray(d.states) ? d.states.map(s => ({
      ...s,
      counties: Array.isArray(s.counties)
        ? s.counties.map(c => ({ ...c, isNew: false }))
        : [],
    })) : [],
  };
};

export default function TaxesSettings({ navigate, isPhone }) {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState(DEFAULTS);
  const [loadedStateIds, setLoadedStateIds] = useState([]);
  const [loadedCountyIds, setLoadedCountyIds] = useState([]);
  const { unsavedWarningVisible, showUnsavedWarning, clearUnsavedWarning } = useTimedUnsavedWarning();

  const [showAddState, setShowAddState] = useState(false);
  const [stateForm, setStateForm] = useState({ state_name: '', state_abbr: '', state_sales_tax_rate: '' });
  const [showAddCountyForState, setShowAddCountyForState] = useState(null);
  const [editingCounty, setEditingCounty] = useState(null);
  const [countyForm, setCountyForm] = useState({ name: '', rate: '' });
  const [showMileageRateForm, setShowMileageRateForm] = useState(false);
  const [editingMileageRate, setEditingMileageRate] = useState(null);
  const [mileageRateForm, setMileageRateForm] = useState({ effective_date: '', rate_per_mile: '' });
  const [savingMileageRate, setSavingMileageRate] = useState(false);
  const [confirmingMileageRateEdit, setConfirmingMileageRateEdit] = useState(null);
  const [deletingMileageRate, setDeletingMileageRate] = useState(null);
  const [deletingMileageRateBusy, setDeletingMileageRateBusy] = useState(false);

  // ── Responsive listener ────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener("resize", onResize);
    setIsMobile(getIsMobile()); // force correct value after mount
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Load settings ──────────────────────────────────────────────────────────
  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/taxes`, {
        headers: { ...getTenantHeaders() }
      });
      if (res.ok) {
        const json = await res.json();
        const next = normalize(json?.data ?? json);
        setSettings(next);
        setLoadedStateIds(next.states.filter(s => s.id).map(s => String(s.id)));
        setLoadedCountyIds(
          next.states.flatMap(s => s.counties).filter(c => c.id).map(c => String(c.id))
        );
        clearUnsavedWarning();
      } else {
        console.error("Failed to load taxes settings:", res.status);
        setSettings(DEFAULTS);
        setLoadedStateIds([]);
        setLoadedCountyIds([]);
      }
    } catch (e) {
      console.error("Failed to load taxes settings:", e);
      setSettings(DEFAULTS);
      setLoadedStateIds([]);
      setLoadedCountyIds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    showUnsavedWarning();
  };

  const openAddMileageRate = () => {
    setEditingMileageRate(null);
    setMileageRateForm({ effective_date: '', rate_per_mile: '' });
    setShowMileageRateForm(true);
  };

  const openEditMileageRate = (rate) => {
    setEditingMileageRate(rate);
    setMileageRateForm({
      effective_date: rate.effective_date,
      rate_per_mile: String(rate.rate_per_mile)
    });
    setShowMileageRateForm(true);
  };

  const closeMileageRateForm = () => {
    if (savingMileageRate) return;
    setShowMileageRateForm(false);
    setEditingMileageRate(null);
    setMileageRateForm({ effective_date: '', rate_per_mile: '' });
  };

  const saveMileageRate = async (confirmedUsedEdit = false) => {
    const effectiveDate = String(mileageRateForm.effective_date || '').trim();
    const ratePerMile = Number(mileageRateForm.rate_per_mile);
    if (!effectiveDate || !Number.isFinite(ratePerMile) || ratePerMile <= 0) {
      alert('Enter a valid effective date and positive mileage rate.');
      return;
    }

    if (editingMileageRate?.is_used) {
      alert('This mileage rate is locked because it is referenced by existing mileage entries.');
      return;
    }

    setSavingMileageRate(true);
    try {
      const isEdit = Boolean(editingMileageRate?.id);
      const url = isEdit
        ? `${API_BASE}/api/settings/taxes/mileage-rates/${editingMileageRate.id}`
        : `${API_BASE}/api/settings/taxes/mileage-rates`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify({
          effective_date: effectiveDate,
          rate_per_mile: ratePerMile,
          ...(isEdit ? { expected_reference_count: editingMileageRate.reference_count } : {})
        })
      });
      if (!res.ok) {
        let data = {};
        try { data = await res.json(); } catch (_e) {}
        const updatedReferenceCount = Number(data?.reference_count);
        if (Number.isInteger(updatedReferenceCount) && updatedReferenceCount >= 0) {
          setEditingMileageRate(prev => prev ? {
            ...prev,
            reference_count: updatedReferenceCount,
            is_used: updatedReferenceCount > 0
          } : prev);
          setConfirmingMileageRateEdit(prev => prev ? {
            ...prev,
            reference_count: updatedReferenceCount,
            is_used: updatedReferenceCount > 0
          } : prev);
        }
        throw new Error(data?.error || 'Mileage rate save failed.');
      }

      setConfirmingMileageRateEdit(null);
      setShowMileageRateForm(false);
      setEditingMileageRate(null);
      setMileageRateForm({ effective_date: '', rate_per_mile: '' });
      await loadSettings();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: isEdit ? 'RATE UPDATED!' : 'RATE CREATED!' }
      }));
    } catch (e) {
      alert(e?.message || 'Mileage rate save failed.');
    } finally {
      setSavingMileageRate(false);
    }
  };

  const deleteMileageRate = async (rate) => {
    setDeletingMileageRateBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/taxes/mileage-rates/${rate.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify({ expected_reference_count: rate.reference_count })
      });
      const data = await res.json();
      if (!res.ok) {
        const updatedReferenceCount = Number(data?.reference_count);
        if (Number.isInteger(updatedReferenceCount) && updatedReferenceCount >= 0) {
          setDeletingMileageRate(prev => prev ? {
            ...prev,
            reference_count: updatedReferenceCount,
            is_used: updatedReferenceCount > 0
          } : prev);
        }
        throw new Error(data?.error || 'Mileage rate delete failed.');
      }

      setDeletingMileageRate(null);
      await loadSettings();
    } catch (e) {
      alert(e?.message || 'Mileage rate delete failed.');
    } finally {
      setDeletingMileageRateBusy(false);
    }
  };

  const requestDeleteMileageRate = (rate) => {
    if (rate.is_used) {
      alert('This mileage rate is locked because it is referenced by existing mileage entries.');
      return;
    }
    if (window.confirm(`DELETE THE MILEAGE RATE EFFECTIVE ${rate.effective_date}?`)) {
      deleteMileageRate(rate);
    }
  };

  // ── Save settings ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    const missingAbbr = settings.states.some(s => !String(s.state_abbr || '').trim());
    if (missingAbbr) {
      alert("All states must have a two-letter abbreviation before saving.");
      return;
    }
    const invalidStateRate = settings.states.some(s => {
      const rawRate = String(s.state_sales_tax_rate ?? "").trim();
      const rate = Number(rawRate);
      return rawRate === "" || !Number.isFinite(rate) || rate < 0;
    });
    if (invalidStateRate) {
      alert("All states must have a valid State Sales Tax rate. Enter 0 if no tax applies.");
      return;
    }
    const invalidCountyRate = settings.states.some(s =>
      (s.counties || []).some(c => {
        const rawRate = String(c.rate ?? "").trim();
        const rate = Number(rawRate);
        return rawRate === "" || !Number.isFinite(rate) || rate < 0;
      })
    );
    if (invalidCountyRate) {
      alert("All counties/municipalities must have a valid tax rate. Enter 0 if no tax applies.");
      return;
    }
    setSaving(true);
    try {
      const retainedStateIds = new Set(
        settings.states.filter(s => s.id).map(s => String(s.id))
      );
      const deleted_state_ids = loadedStateIds.filter(id => !retainedStateIds.has(id));

      const retainedCountyIds = new Set(
        settings.states
          .flatMap(s => s.counties || [])
          .filter(c => !c.isNew && c.id)
          .map(c => String(c.id))
      );
      const deleted_county_ids = loadedCountyIds.filter(id => !retainedCountyIds.has(id));

      const res = await fetch(`${API_BASE}/api/settings/taxes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify({ ...settings, deleted_state_ids, deleted_county_ids })
      });

      if (!res.ok) {
        throw new Error(`Save failed: ${res.status}`);
      }

      clearUnsavedWarning();
      await loadSettings();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'TAXES SETTINGS SAVED!' }
      }));
    } catch (e) {
      console.error("Save error:", e);
      alert("Failed to save settings: " + (e?.message || "Unknown error"));
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  // ── State operations ───────────────────────────────────────────────────────
  const addState = () => {
    const state_name = stateForm.state_name.trim();
    const state_abbr = stateForm.state_abbr.trim().toUpperCase().slice(0, 2);
    const rawStateSalesTaxRate = String(stateForm.state_sales_tax_rate ?? "").trim();
    const state_sales_tax_rate = Number(rawStateSalesTaxRate);

    if (!state_name) return alert("Enter state name");
    if (rawStateSalesTaxRate === "") return alert("Enter State Sales Tax rate. Enter 0 if no tax applies.");
    if (!Number.isFinite(state_sales_tax_rate) || state_sales_tax_rate < 0) return alert("Enter valid state sales tax rate");

    const isFirst = settings.states.length === 0;
    setSettings(prev => ({
      ...prev,
      states: [...prev.states, { id: null, state_name, state_abbr, state_sales_tax_rate, is_home_state: isFirst ? 1 : 0, counties: [] }],
    }));
    showUnsavedWarning();
    setStateForm({ state_name: '', state_abbr: '', state_sales_tax_rate: '' });
    setShowAddState(false);
  };

  const deleteState = (idx) => {
    if (!window.confirm("DELETE THIS STATE AND ALL ITS COUNTIES?")) return;
    setSettings(prev => {
      const newStates = prev.states.filter((_, i) => i !== idx);
      if (prev.states[idx].is_home_state && newStates.length > 0) {
        newStates[0] = { ...newStates[0], is_home_state: 1 };
      }
      return { ...prev, states: newStates };
    });
    showUnsavedWarning();
  };

  const setHomeState = (idx) => {
    setSettings(prev => ({
      ...prev,
      states: prev.states.map((s, i) => ({ ...s, is_home_state: i === idx ? 1 : 0 })),
    }));
    showUnsavedWarning();
  };

  const updateStateField = (idx, key, value) => {
    setSettings(prev => ({
      ...prev,
      states: prev.states.map((s, i) => i === idx ? { ...s, [key]: value } : s),
    }));
    showUnsavedWarning();
  };

  // ── County operations (per-state) ──────────────────────────────────────────
  const saveAddCounty = () => {
    const name = countyForm.name.trim();
    const rawRate = String(countyForm.rate ?? "").trim();
    const rate = Number(rawRate);

    if (!name) return alert("Enter county/municipality name");
    if (rawRate === "") return alert("Enter tax rate. Enter 0 if no tax applies.");
    if (!Number.isFinite(rate) || rate < 0) return alert("Enter valid tax rate");

    setSettings(prev => ({
      ...prev,
      states: prev.states.map((s, i) => {
        if (i !== showAddCountyForState) return s;
        return { ...s, counties: [...s.counties, { id: Date.now(), isNew: true, name, rate, is_default: 0 }] };
      }),
    }));
    showUnsavedWarning();
    setCountyForm({ name: '', rate: '' });
    setShowAddCountyForState(null);
  };

  const startEditCounty = (stateIdx, countyId) => {
    const county = settings.states[stateIdx].counties.find(c => c.id === countyId);
    setEditingCounty({ stateIdx, countyId });
    setCountyForm({ name: county.name, rate: String(county.rate) });
  };

  const saveEditCounty = () => {
    const name = countyForm.name.trim();
    const rawRate = String(countyForm.rate ?? "").trim();
    const rate = Number(rawRate);

    if (!name) return alert("Enter county/municipality name");
    if (rawRate === "") return alert("Enter tax rate. Enter 0 if no tax applies.");
    if (!Number.isFinite(rate) || rate < 0) return alert("Enter valid tax rate");

    setSettings(prev => ({
      ...prev,
      states: prev.states.map((s, i) => {
        if (i !== editingCounty.stateIdx) return s;
        return { ...s, counties: s.counties.map(c => c.id === editingCounty.countyId ? { ...c, name, rate } : c) };
      }),
    }));
    showUnsavedWarning();
    setEditingCounty(null);
    setCountyForm({ name: '', rate: '' });
  };

  const deleteCounty = (stateIdx, countyId) => {
    if (!window.confirm("DELETE THIS COUNTY/MUNICIPALITY TAX?")) return;
    setSettings(prev => ({
      ...prev,
      states: prev.states.map((s, i) => {
        if (i !== stateIdx) return s;
        return { ...s, counties: s.counties.filter(c => c.id !== countyId) };
      }),
    }));
    showUnsavedWarning();
  };

  const setDefaultCounty = (stateIdx, countyId) => {
    setSettings(prev => ({
      ...prev,
      states: prev.states.map((s, i) => {
        if (i !== stateIdx) return s;
        return { ...s, counties: s.counties.map(c => ({ ...c, is_default: c.id === countyId ? 1 : 0 })) };
      }),
    }));
    showUnsavedWarning();
  };

  const styles = {
    page: {
      padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    contentWrapper: {
      maxWidth: "1200px", padding: isPhone ? '0' : "0 12px",
      margin: '0 auto'
    },
    panel: {
      backgroundColor: "#E7B888",
      border: isMobile ? "6px solid black" : "10px solid black",
      padding: isPhone ? "15px 5px" : (isMobile ? "15px" : "24px"),
      marginBottom: "18px",
    },
    title: {
      fontWeight: 900,
      textTransform: "uppercase",
      fontSize: isMobile ? "22px" : "32px",
      margin: "0 0 14px 0",
    },
    sectionTitle: {
      fontWeight: 900,
      textTransform: "uppercase",
      fontSize: "18px",
      margin: "20px 0 14px 0",
      paddingTop: "20px",
      borderTop: "4px solid black",
    },
    label: {
      fontWeight: 900,
      textTransform: "uppercase",
      fontSize: "13px",
      marginBottom: "6px",
      display: "block",
    },
    input: {
      padding: "14px",
      border: "4px solid black",
      fontWeight: 900,
      width: "100%",
      fontSize: "16px",
      boxSizing: "border-box",
    },
    row2: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: "12px",
      marginBottom: "12px",
    },
    row1: { marginBottom: "12px" },
    saveBtn: {
      background: "black",
      color: "#FFA500",
      border: "5px solid #E8761D",
      padding: "16px 24px",
      fontWeight: 900,
      cursor: "pointer",
      textTransform: "uppercase",
      fontSize: "18px",
      width: isMobile ? "100%" : "auto",
    },
    addBtn: {
      background: "black",
      color: "#FFA500",
      border: "4px solid #E8761D",
      padding: "12px 20px",
      fontWeight: 900,
      cursor: "pointer",
      textTransform: "uppercase",
      fontSize: "14px",
      marginBottom: "12px",
    },
    countyCard: {
      border: "4px solid black",
      padding: "14px",
      marginBottom: "12px",
      backgroundColor: "white",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "10px",
    },
    countyInfo: { fontWeight: 900, flex: 1, minWidth: "200px" },
    countyActions: { display: "flex", gap: "8px", flexWrap: "wrap" },
    mileageRateHeader: {
      display: isMobile ? "none" : "grid",
      gridTemplateColumns: "1fr 1fr 2fr",
      gap: "10px",
      backgroundColor: "black",
      color: "#FFA500",
      border: "4px solid black",
      padding: "10px 14px",
      fontWeight: 900,
      textTransform: "uppercase",
      fontSize: "12px",
    },
    mileageRateRow: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 2fr",
      gap: "10px",
      alignItems: "center",
      backgroundColor: "white",
      border: "4px solid black",
      borderTop: "none",
      padding: "12px 14px",
      fontWeight: 900,
    },
    lockedBadge: {
      display: "inline-block",
      backgroundColor: "#ffffcc",
      border: "3px solid black",
      padding: "6px 10px",
      fontWeight: 900,
      fontSize: "11px",
      textTransform: "uppercase",
    },
    editBtn: {
      background: "white",
      color: "black",
      border: "3px solid black",
      padding: "8px 14px",
      fontWeight: 900,
      cursor: "pointer",
      textTransform: "uppercase",
      fontSize: "13px",
    },
    deleteBtn: {
      background: "#ff4444",
      color: "white",
      border: "3px solid black",
      padding: "8px 14px",
      fontWeight: 900,
      cursor: "pointer",
      textTransform: "uppercase",
      fontSize: "13px",
    },
    modal: {
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.85)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 5000,
      padding: "20px",
    },
    modalContent: {
      backgroundColor: "white",
      border: "8px solid black",
      padding: "24px",
      maxWidth: "500px",
      width: "100%",
    },
    modalTitle: {
      fontWeight: 900,
      fontSize: "22px",
      marginBottom: "16px",
      textTransform: "uppercase",
    },
    modalBtnRow: { display: "flex", gap: "10px", marginTop: "16px" },
    confirmBtn: {
      flex: 1,
      background: "black",
      color: "#FFA500",
      border: "4px solid #E8761D",
      padding: "12px",
      fontWeight: 900,
      cursor: "pointer",
      textTransform: "uppercase",
    },
    cancelBtn: {
      flex: 1,
      background: "white",
      color: "black",
      border: "4px solid black",
      padding: "12px",
      fontWeight: 900,
      cursor: "pointer",
      textTransform: "uppercase",
    },
    note: {
      fontWeight: 900,
      fontSize: "13px",
      padding: "12px",
      border: "3px solid black",
      backgroundColor: "#ffffcc",
      marginBottom: "12px",
    },
    loadingBox: {
      padding: '30px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: '16px'
    },
    statePanel: {
      border: "4px solid black",
      backgroundColor: "white",
      padding: isMobile ? "14px" : "20px",
      marginBottom: "16px",
    },
    statePanelHeader: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      flexWrap: "wrap",
      marginBottom: "14px",
    },
    homeBadge: {
      background: "#39FF14",
      color: "black",
      border: "3px solid black",
      padding: "6px 12px",
      fontWeight: 900,
      fontSize: "13px",
      textTransform: "uppercase",
    },
    setHomeBtn: {
      background: "white",
      color: "black",
      border: "3px solid black",
      padding: "6px 12px",
      fontWeight: 900,
      cursor: "pointer",
      textTransform: "uppercase",
      fontSize: "13px",
    },
    deleteStateBtn: {
      background: "#ff4444",
      color: "white",
      border: "3px solid black",
      padding: "6px 12px",
      fontWeight: 900,
      cursor: "pointer",
      textTransform: "uppercase",
      fontSize: "13px",
    },
    defaultBadge: {
      display: "inline-block",
      background: "#f59e0b",
      color: "black",
      border: "2px solid black",
      padding: "3px 8px",
      fontWeight: 900,
      fontSize: "11px",
      textTransform: "uppercase",
      marginTop: "4px",
    },
    setDefaultBtn: {
      background: "#f59e0b",
      color: "black",
      border: "3px solid black",
      padding: "6px 12px",
      fontWeight: 900,
      cursor: "pointer",
      textTransform: "uppercase",
      fontSize: "12px",
    },
    warningBox: {
      fontWeight: 900,
      fontSize: "13px",
      padding: "10px 12px",
      border: "3px solid #f59e0b",
      backgroundColor: "#fffde7",
      marginBottom: "12px",
    },
    countySubheader: {
      fontWeight: 900,
      fontSize: "14px",
      textTransform: "uppercase",
      marginBottom: "10px",
      paddingTop: "14px",
      borderTop: "3px solid black",
      marginTop: "8px",
    },
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jf-tax-hover:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
      `}</style>
      <div style={styles.contentWrapper}>
        <TitleBar
          icon="💵"
          title="TAXES SETUP"
          accent="#E8761D"
          right={
            <button
              className="jf-tax-hover"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING TAXES SETTINGS...</div>
          ) : (
            <>
              <div style={styles.note}>
                Configure your tax rates and deductions. These rates are used in tax calculations and invoicing.
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>SELF-EMPLOYMENT TAX RATE (%)</label>
                  <input
                    style={styles.input}
                    type="number"
                    step="0.1"
                    value={settings.self_employment_tax_rate}
                    onChange={(e) => setField("self_employment_tax_rate", Number(e.target.value))}
                  />
                </div>

                <div>
                  <label style={styles.label}>STATE INCOME TAX RATE (%)</label>
                  <input
                    style={styles.input}
                    type="number"
                    step="0.1"
                    value={settings.state_income_tax_rate}
                    onChange={(e) => setField("state_income_tax_rate", Number(e.target.value))}
                  />
                </div>
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>MILEAGE RATES<RequiredMark /></label>
                <div style={styles.mileageRateHeader}>
                  <div>EFFECTIVE DATE</div>
                  <div>RATE</div>
                  <div>ACTIONS</div>
                </div>
                {settings.mileage_rates.length === 0 ? (
                  <div style={{ ...styles.note, backgroundColor: "white", marginBottom: "12px" }}>
                    No mileage rates configured yet.
                  </div>
                ) : (
                  settings.mileage_rates.map(rate => (
                    <div key={rate.id} style={styles.mileageRateRow}>
                      <div>
                        {isMobile && <div style={styles.label}>EFFECTIVE DATE</div>}
                        {rate.effective_date}
                      </div>
                      <div>
                        {isMobile && <div style={styles.label}>RATE</div>}
                        ${Number(rate.rate_per_mile).toFixed(3)} / mile
                      </div>
                      <div>
                        {isMobile && <div style={styles.label}>ACTIONS</div>}
                        <div style={styles.countyActions}>
                        {rate.is_used && (
                          <span style={styles.lockedBadge}>
                            LOCKED — {rate.reference_count} {rate.reference_count === 1 ? 'ENTRY' : 'ENTRIES'}
                          </span>
                        )}
                        {!rate.is_used && (
                          <>
                            <button className="jf-tax-hover" style={styles.editBtn} onClick={() => openEditMileageRate(rate)}>EDIT</button>
                            <button className="jf-tax-hover" style={styles.deleteBtn} onClick={() => requestDeleteMileageRate(rate)}>DELETE</button>
                          </>
                        )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <button className="jf-tax-hover" style={{ ...styles.addBtn, marginTop: "12px" }} onClick={openAddMileageRate}>
                  + ADD MILEAGE RATE
                </button>
              </div>

              <h2 style={styles.sectionTitle}>STATES & COUNTIES / LOCAL SALES TAXES</h2>

              {settings.states.length === 0 ? (
                <div style={{ ...styles.note, backgroundColor: "white" }}>
                  No states configured yet. Add a state to set up sales tax.
                </div>
              ) : (
                settings.states.map((state, idx) => (
                  <div key={idx} style={styles.statePanel}>
                    {(!String(state.state_abbr || '').trim() || !String(state.state_name || '').trim()) && (
                      <div style={styles.warningBox}>
                        ⚠ Please update your state name and two-letter abbreviation.
                      </div>
                    )}

                    <div style={styles.statePanelHeader}>
                      {state.is_home_state ? (
                        <span style={styles.homeBadge}>HOME STATE<RequiredMark /></span>
                      ) : (
                        <button className="jf-tax-hover" style={styles.setHomeBtn} onClick={() => setHomeState(idx)}>
                          SET AS HOME STATE<RequiredMark />
                        </button>
                      )}
                      <button className="jf-tax-hover" style={styles.deleteStateBtn} onClick={() => deleteState(idx)}>
                        DELETE STATE
                      </button>
                    </div>

                    <div style={styles.row2}>
                      <div>
                        <label style={styles.label}>STATE NAME</label>
                        <input
                          style={styles.input}
                          value={state.state_name}
                          onChange={(e) => updateStateField(idx, 'state_name', e.target.value)}
                          placeholder="e.g., Florida"
                        />
                      </div>
                      <div>
                        <label style={styles.label}>STATE ABBREVIATION (2 LETTERS)</label>
                        <input
                          style={styles.input}
                          value={state.state_abbr}
                          maxLength={2}
                          onChange={(e) => updateStateField(idx, 'state_abbr', e.target.value.toUpperCase().slice(0, 2))}
                          placeholder="e.g., FL"
                        />
                      </div>
                    </div>

                    <div style={styles.row1}>
                      <label style={styles.label}>STATE SALES TAX RATE (%)<RequiredMark /></label>
                      <input
                        style={styles.input}
                        type="number"
                        step="0.01"
                        value={state.state_sales_tax_rate}
                        placeholder="0.00"
                        onChange={(e) => updateStateField(idx, 'state_sales_tax_rate', e.target.value)}
                      />
                    </div>

                    <div style={styles.countySubheader}>COUNTY / LOCAL TAXES<RequiredMark /></div>

                    <button
                      className="jf-tax-hover"
                      style={styles.addBtn}
                      onClick={() => { setShowAddCountyForState(idx); setCountyForm({ name: '', rate: '' }); }}
                    >
                      + ADD COUNTY/MUNICIPALITY
                    </button>

                    {state.counties.length === 0 ? (
                      <div style={{ ...styles.note, backgroundColor: "white" }}>
                        No county taxes configured for this state.
                      </div>
                    ) : (
                      state.counties.map((county) => (
                        <div key={county.id} style={styles.countyCard}>
                          <div style={styles.countyInfo}>
                            <div style={{ fontSize: "18px" }}>{county.name}</div>
                            <div style={{ fontSize: "16px", marginTop: "4px" }}>
                              TAX RATE: {Number(county.rate).toFixed(2)}%
                            </div>
                            {county.is_default ? <span style={styles.defaultBadge}>DEFAULT<RequiredMark /></span> : null}
                          </div>
                          <div style={styles.countyActions}>
                            {!county.is_default && (
                              <button className="jf-tax-hover" style={styles.setDefaultBtn} onClick={() => setDefaultCounty(idx, county.id)}>
                                SET DEFAULT<RequiredMark />
                              </button>
                            )}
                            <button className="jf-tax-hover" style={styles.editBtn} onClick={() => startEditCounty(idx, county.id)}>
                              EDIT
                            </button>
                            <button className="jf-tax-hover" style={styles.deleteBtn} onClick={() => deleteCounty(idx, county.id)}>
                              DELETE
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ))
              )}

              <button
                className="jf-tax-hover"
                style={styles.addBtn}
                onClick={() => { setShowAddState(true); setStateForm({ state_name: '', state_abbr: '', state_sales_tax_rate: '' }); }}
              >
                + ADD STATE
              </button>

              <div style={{ marginTop: "24px" }}>
                {unsavedWarningVisible && (
                  <NotificationOverlayFrame accentColor="#FFC107" role="status" ariaLive="polite">
                    <div style={{ fontWeight: 900, fontSize: "clamp(18px, 4.8vw, 22px)", lineHeight: 1.2 }}>
                      TAX CHANGES NOT SAVED YET
                    </div>
                    <div style={{ fontWeight: 700, fontSize: "clamp(15px, 4vw, 18px)", lineHeight: 1.35, marginTop: "6px" }}>
                      Select SAVE TAXES SETTINGS to keep your changes.
                    </div>
                  </NotificationOverlayFrame>
                )}
                <button className="jf-tax-hover" style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? "SAVING..." : "SAVE TAXES SETTINGS"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showMileageRateForm && !confirmingMileageRateEdit && (
        <div style={styles.modal} onClick={closeMileageRateForm}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>{editingMileageRate ? 'EDIT MILEAGE RATE' : 'ADD MILEAGE RATE'}</h2>

            <div style={styles.row1}>
              <label style={styles.label}>EFFECTIVE DATE<RequiredMark /></label>
              <input
                style={styles.input}
                type="date"
                value={mileageRateForm.effective_date}
                onChange={(e) => setMileageRateForm({ ...mileageRateForm, effective_date: e.target.value })}
              />
            </div>

            <div style={styles.row1}>
              <label style={styles.label}>RATE<RequiredMark /></label>
              <input
                style={styles.input}
                type="number"
                min="0.000001"
                step="0.001"
                value={mileageRateForm.rate_per_mile}
                onChange={(e) => setMileageRateForm({ ...mileageRateForm, rate_per_mile: e.target.value })}
                placeholder="0.000"
              />
            </div>

            <div style={styles.modalBtnRow}>
              <button className="jf-tax-hover" style={styles.confirmBtn} onClick={() => saveMileageRate()} disabled={savingMileageRate}>
                {savingMileageRate ? 'SAVING...' : editingMileageRate ? 'SAVE' : 'ADD'}
              </button>
              <button className="jf-tax-hover" style={styles.cancelBtn} onClick={closeMileageRateForm} disabled={savingMileageRate}>
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmingMileageRateEdit && (
        <div style={styles.modal} onClick={() => { if (!savingMileageRate) setConfirmingMileageRateEdit(null); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>EDIT MILEAGE RATE</h2>
            <div style={styles.note}>
              This mileage rate is currently referenced by exactly {confirmingMileageRateEdit.reference_count} mileage {confirmingMileageRateEdit.reference_count === 1 ? 'entry' : 'entries'}.
            </div>
            <div style={styles.warningBox}>
              Changing the effective date or rate may recalculate existing mileage entries using the resulting effective-dated mileage schedule. Only mileage_rate_id and rate_applied will change. All other mileage data will remain unchanged.
            </div>
            <div style={styles.modalBtnRow}>
              <button
                className="jf-tax-hover"
                style={styles.deleteBtn}
                onClick={() => saveMileageRate(true)}
                disabled={savingMileageRate}
              >
                {savingMileageRate ? 'SAVING...' : 'CONFIRM EDIT'}
              </button>
              <button
                className="jf-tax-hover"
                style={styles.cancelBtn}
                onClick={() => setConfirmingMileageRateEdit(null)}
                disabled={savingMileageRate}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {deletingMileageRate && (
        <div style={styles.modal} onClick={() => { if (!deletingMileageRateBusy) setDeletingMileageRate(null); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>DELETE MILEAGE RATE</h2>
            <div style={styles.note}>
              This mileage rate is referenced by exactly {deletingMileageRate.reference_count} mileage {deletingMileageRate.reference_count === 1 ? 'entry' : 'entries'}.
            </div>
            <div style={styles.warningBox}>
              Deleting it will recalculate those entries using the applicable remaining effective-dated mileage schedule. Only mileage_rate_id and rate_applied will change. All other mileage data will remain unchanged.
            </div>
            <div style={styles.modalBtnRow}>
              <button
                className="jf-tax-hover"
                style={styles.deleteBtn}
                onClick={() => deleteMileageRate(deletingMileageRate)}
                disabled={deletingMileageRateBusy}
              >
                {deletingMileageRateBusy ? 'DELETING...' : 'CONFIRM DELETE'}
              </button>
              <button
                className="jf-tax-hover"
                style={styles.cancelBtn}
                onClick={() => setDeletingMileageRate(null)}
                disabled={deletingMileageRateBusy}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddState && (
        <div style={styles.modal} onClick={() => setShowAddState(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>ADD STATE</h2>

            <div style={styles.row1}>
              <label style={styles.label}>STATE NAME</label>
              <input
                style={styles.input}
                value={stateForm.state_name}
                onChange={(e) => setStateForm({ ...stateForm, state_name: e.target.value })}
                placeholder="e.g., Florida"
              />
            </div>

            <div style={styles.row1}>
              <label style={styles.label}>STATE ABBREVIATION (2 LETTERS)</label>
              <input
                style={styles.input}
                value={stateForm.state_abbr}
                maxLength={2}
                onChange={(e) => setStateForm({ ...stateForm, state_abbr: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="e.g., FL"
              />
            </div>

            <div style={styles.row1}>
              <label style={styles.label}>STATE SALES TAX RATE (%)<RequiredMark /></label>
              <input
                style={styles.input}
                type="number"
                step="0.01"
                value={stateForm.state_sales_tax_rate}
                onChange={(e) => setStateForm({ ...stateForm, state_sales_tax_rate: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div style={styles.modalBtnRow}>
              <button className="jf-tax-hover" style={styles.confirmBtn} onClick={addState}>
                ADD STATE
              </button>
              <button
                className="jf-tax-hover"
                style={styles.cancelBtn}
                onClick={() => { setShowAddState(false); setStateForm({ state_name: '', state_abbr: '', state_sales_tax_rate: '' }); }}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddCountyForState !== null && (
        <div style={styles.modal} onClick={() => setShowAddCountyForState(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>ADD COUNTY/MUNICIPALITY TAX</h2>

            <div style={styles.row1}>
              <label style={styles.label}>COUNTY/MUNICIPALITY NAME</label>
              <input
                style={styles.input}
                value={countyForm.name}
                onChange={(e) => setCountyForm({ ...countyForm, name: e.target.value })}
                placeholder="e.g., Lee County"
              />
            </div>

            <div style={styles.row1}>
              <label style={styles.label}>TAX RATE (%)<RequiredMark /></label>
              <input
                style={styles.input}
                type="number"
                step="0.01"
                value={countyForm.rate}
                onChange={(e) => setCountyForm({ ...countyForm, rate: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div style={styles.modalBtnRow}>
              <button className="jf-tax-hover" style={styles.confirmBtn} onClick={saveAddCounty}>
                ADD
              </button>
              <button
                className="jf-tax-hover"
                style={styles.cancelBtn}
                onClick={() => { setShowAddCountyForState(null); setCountyForm({ name: '', rate: '' }); }}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCounty !== null && (
        <div style={styles.modal} onClick={() => setEditingCounty(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>EDIT COUNTY/MUNICIPALITY TAX</h2>

            <div style={styles.row1}>
              <label style={styles.label}>COUNTY/MUNICIPALITY NAME</label>
              <input
                style={styles.input}
                value={countyForm.name}
                onChange={(e) => setCountyForm({ ...countyForm, name: e.target.value })}
              />
            </div>

            <div style={styles.row1}>
              <label style={styles.label}>TAX RATE (%)<RequiredMark /></label>
              <input
                style={styles.input}
                type="number"
                step="0.01"
                value={countyForm.rate}
                onChange={(e) => setCountyForm({ ...countyForm, rate: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div style={styles.modalBtnRow}>
              <button className="jf-tax-hover" style={styles.confirmBtn} onClick={saveEditCounty}>
                SAVE
              </button>
              <button
                className="jf-tax-hover"
                style={styles.cancelBtn}
                onClick={() => { setEditingCounty(null); setCountyForm({ name: '', rate: '' }); }}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
